import { faCloudUpload } from "@fortawesome/pro-regular-svg-icons/faCloudUpload";
import { faImages } from "@fortawesome/pro-regular-svg-icons/faImages";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AttachType } from "app/appModels";
import { AsyncUploader } from "components/AsyncUploader";
import { UploadCallback, UploadRemoveCallback, UploadStatusChange } from "components/FileUpload/AttachSelection";
import { IconButtonCancel } from "components/iconButtons";
import { ImageSelectionResult, MprcImageSelectionDialog } from "components/MprcImageHandler";
import { onImageSelectionUploadError } from "components/MprcImageHandler/MprcImageSelectionDialog";
import { AsyncUploadResult, GetPublicationAttachDto, StorageMetadataModel } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import LibUtils from "lib/LibUtils";
import MediaUtils from 'lib/MediaUtils';
import { useSnackbar } from "notistack";
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import Dropzone, { Accept } from 'react-dropzone';
import { useTranslation } from "react-i18next";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
  publicationId: string;
  readOnly?: boolean;

  attachs: GetPublicationAttachDto[];
  allowAttachVideo?: boolean;

  additionalLeftButton?: React.ReactElement[];


  maxSize?: number;

  onUploadStatusChange?: UploadStatusChange;
  onUploadComplete?: UploadCallback;
  onUploadRemove?: UploadRemoveCallback;
  onMetadataReceived?: (data: StorageMetadataModel) => void;
  onPool?: () => void;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const PublicationAttachFileHandler = (props: IProps) => {

  const theme = useTheme();
  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();

  const [progressBar, setProgressBar] = useState<number | null>(null);
  const [dialog, setDialog] = useState<any>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const addBoxRef = useRef<HTMLDivElement | null>(null);

  const { additionalLeftButton, readOnly, maxSize, allowAttachVideo = false } = props;

  const acceptedTypes = [AttachType.Document, AttachType.Image, AttachType.Audio];
  if (allowAttachVideo) acceptedTypes.push(AttachType.Video);

  const acceptsImage = acceptedTypes.includes(AttachType.Image);
  const acceptsVideo = acceptedTypes.includes(AttachType.Video);
  const acceptsAudio = acceptedTypes.includes(AttachType.Audio);
  const acceptsDocument = acceptedTypes.includes(AttachType.Document);

  const dropAcceptFiles: Accept = {};
  if (acceptsImage) dropAcceptFiles["image/*"] = MediaUtils.AcceptImages();
  if (acceptsAudio) dropAcceptFiles["audio/*"] = MediaUtils.AcceptAudios();
  if (acceptsVideo) dropAcceptFiles["video/*"] = MediaUtils.AcceptVideos();
  if (acceptsDocument) {
    for (var docType of MediaUtils.AcceptDocuments())
      dropAcceptFiles[`application/${docType.replace(".", "")}`] = [docType];
  }

  const inputAcceptFiles: string[] = [];
  if (acceptsImage) inputAcceptFiles.push(...MediaUtils.AcceptImages());
  if (acceptsAudio) inputAcceptFiles.push(...MediaUtils.AcceptAudios());
  if (acceptsVideo) inputAcceptFiles.push(...MediaUtils.AcceptVideos());
  if (acceptsDocument) inputAcceptFiles.push(...MediaUtils.AcceptDocuments());

  ///////////////////////////////////////////////////////////////////////////

  const getFilesizeErrorMessage = (fileSize: number) => {
    return t("AsyncUpload.MaxSizeExceeded", { fileSize: Formaters.fileSize(fileSize), maxSize: Formaters.fileSize(maxSize), ns: "shared" });
  };

  ///
  const loadAttachStarted = () => {
    if (props.onUploadStatusChange) props.onUploadStatusChange("uploading");
  };

  ///
  const loadAttachProgress = (progress: number) => {
    setProgressBar(progress);
  };

  ///
  const loadAttachSuccess = (data: AsyncUploadResult) => {
    if (props.onUploadStatusChange) props.onUploadStatusChange("uploaded");

    if (data.error) {
      setProgressBar(null);
      enqueueSnackbar(t("AsyncUpload.UploadProccessFailed", { ns: "shared" }), { variant: "error" });
    }
    else {
      setProgressBar(null);
      if (props.onUploadComplete) props.onUploadComplete(data);
    }
  };

  ///
  const loadAttachError = (instance: AsyncUploader, thrown: any) => {
    if (props.onUploadStatusChange) props.onUploadStatusChange("canceled");
    onImageSelectionUploadError(instance, thrown, t);

    setProgressBar(null);
  };

  const asyncUploader = useRef<AsyncUploader>(new AsyncUploader({
    onFileUploadProgress: loadAttachProgress,
    onFileUploadSuccess: loadAttachSuccess,
    onFileUploadError: loadAttachError,
    onFileStarted: loadAttachStarted,
  }));

  ///////////////////////////////////////////////////////////////////////////

  ///
  const doLoadImage = (image: ImageSelectionResult | null) => {
    if (image != null) {
      var finalImage = image.editedImage ?? image.source;
      asyncUploader.current.uploadImage(finalImage, true);
    }
    setDialog(null);
  };

  ///
  const doLoadAttach = (file: File) => {
    asyncUploader.current.uploadSingleFile(file, { "createThumbnail": "1" });
  };

  ///
  const handleImageSelection = (initialFile?: File) => {
    setDialog(
      <MprcImageSelectionDialog
        acceptedFiles={MediaUtils.DropzoneAcceptImages()}
        initialSource="library"
        initialFile={initialFile}
        backgroundType="solid"
        allowEdit={false}
        maxSize={maxSize}
        onClose={doLoadImage}
      />
    );
  };

  ///
  const renderLeft = () => {
    const cloudTooltip = acceptsDocument && acceptsVideo && acceptsAudio && acceptsImage
      ? t("AsyncUpload.SelectAllTypesTooltip", { ns: "shared" })
      : (
        t("AsyncUpload.SelectionText", { ns: "shared" })
        + ": "
        + [
          (acceptsDocument && t("AsyncUpload.DocumentTypeText", { ns: "shared" }))
          , (acceptsImage && t("AsyncUpload.ImageTypeText", { ns: "shared" }))
          , (acceptsVideo && t("AsyncUpload.VideoTypeText", { ns: "shared" }))
          , (acceptsAudio && t("AsyncUpload.AudioTypeText", { ns: "shared" }))
        ].join(", ")
      );

    return (
      <div>
        {
          additionalLeftButton
        }

        {
          acceptsImage &&
          <IconButton
            key="handleImage"
            title={t("Publication.Common.Editor.ImageLibraryTooltip")}
            disabled={readOnly}
            onClick={() => { handleImageSelection(); }}
          >
            <FontAwesomeIcon color="inherit" icon={faImages} fixedWidth />
          </IconButton>
        }

        {
          (acceptsDocument || acceptsVideo || acceptsAudio) &&
          <IconButton
            key="cloudUpload"
            title={cloudTooltip}
            disabled={readOnly}
            onClick={() => fileInputRef.current!.click()}
          >
            <FontAwesomeIcon color="inherit" icon={faCloudUpload} fixedWidth />
          </IconButton>
        }

        <input
          type="file"
          accept={inputAcceptFiles.join(",")}
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={
            (e) => {
              if (e.target.files) {
                var f = e.target.files[0];
                if (maxSize && f.size > maxSize) {
                  enqueueSnackbar(getFilesizeErrorMessage(e.target.files.length), { variant: "error" });
                }
                else if (!LibUtils.isFileAccept(f, inputAcceptFiles)) {
                  if (acceptsDocument)
                    enqueueSnackbar(t("AsyncUpload.FileDocumentNotAccept", { ns: "shared" }), { variant: "error" });
                  else
                    enqueueSnackbar(t("AsyncUpload.FileTypeNotAccept", { ns: "shared" }), { variant: "error" });
                }
                else
                  doLoadAttach(f);
              }
              fileInputRef.current!.value = "";
            }
          }
        />
      </div>
    );
  };

  ///
  const renderRight = () => {

    if (readOnly) return null;

    if (progressBar) {
      return (
        <Box
          sx={
            (theme) => ({
              borderStyle: "solid",
              borderWidth: 1,
              borderColor: theme.palette.warning.dark,
              backgroundColor: theme.palette.notice.main,
              padding: 1
            })
          }
        >
          <Typography variant="body2" color="warning.main">
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid>
                {t("AsyncUpload.Loading", { ns: "shared" })}
              </Grid>
              <Grid>
                <b>{progressBar + "%"}</b>
              </Grid>
              <Grid>
                <IconButtonCancel
                  size="small"
                  onClick={() => asyncUploader.current.cancel()}
                />
              </Grid>
            </Grid>
          </Typography>
        </Box>
      );
    }

    return (
      <Dropzone
        accept={dropAcceptFiles}
        multiple={false}
        maxSize={maxSize}
        onDropRejected={
          (files) => {
            if (files[0].errors[0].code == "file-too-large")
              enqueueSnackbar(getFilesizeErrorMessage(files[0].file.size), { variant: "error" });
            else if (acceptsDocument) {
              if (MediaUtils.isVideo(files[0].file.type) && !allowAttachVideo) {
                enqueueSnackbar(t("AsyncUpload.NotAllowAttachVideo", { ns: "shared" }), { variant: "error" });
              }
              else {
                enqueueSnackbar(t("AsyncUpload.FileDocumentNotAccept", { ns: "shared" }), { variant: "error" });
              }
            }
            else
              enqueueSnackbar(t("AsyncUpload.FileTypeNotAccept", { ns: "shared" }), { variant: "error" });
          }
        }
        onDropAccepted={(e) => doLoadAttach(e[0])}
      >
        {
          ({ getRootProps, getInputProps, isDragActive }) => {
            return (
              <Box
                {...getRootProps(
                  {
                    style: {
                      cursor: "pointer",
                      borderStyle: "solid",
                      borderWidth: 1,
                      borderColor: isDragActive ? "transparent" : theme.palette.muted.light,
                      backgroundColor: theme.palette.notice.main,
                      padding: theme.spacing(1),
                      textAlign: "center",
                      "& > input[type=file]": {
                        display: "none"
                      }
                    }
                  }
                )}
                className={isDragActive ? AppGlobalClassKeys.borderAnts : undefined}
              >
                <input {...getInputProps()} />
                <Typography variant="body2">
                  {
                    acceptsVideo
                      ? t("Publication.CreatePost.DropzoneText")
                      : t("Publication.CreatePost.DropzoneRestrictText")
                  }
                </Typography>
              </Box>
            );
          }
        }
      </Dropzone>

    );
  };

  //////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    var allTypes = [];
    if (acceptsImage) allTypes.push(...MediaUtils.AcceptImages());
    if (acceptsVideo) allTypes.push(...MediaUtils.AcceptVideos());
    if (acceptsAudio) allTypes.push(...MediaUtils.AcceptAudios());
    if (acceptsDocument) allTypes.push(...MediaUtils.AcceptDocuments());

    fileInputRef.current!.accept = allTypes?.join(",");
  }, [acceptedTypes, fileInputRef.current]);

  useEffect(() => {
    setTimeout(() => addBoxRef.current && addBoxRef.current.scrollIntoView(false), 100);
  }, []);

  //////////////////////////////////////////////////////////////////////////////
  /// RENDER
  //////////////////////////////////////////////////////////////////////////////

  if (acceptedTypes.length == 0) return null;

  return (
    <div>
      {dialog}
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid>
          {renderLeft()}
        </Grid>
        <Grid size="grow">
          {renderRight()}
        </Grid>
      </Grid>
    </div>
  );
};
